import { useEffect, useMemo, useState } from 'react'
import {
  formatRunElapsed,
  isActiveRunStatus,
  resolveRunProgressStep,
  RUN_PROGRESS_STEPS,
  type ComplianceRunStage,
} from '../services/complianceV2'
import './RunProgressStepper.css'

const STEP_LABELS: Record<ComplianceRunStage, string> = {
  Queued: 'Queued',
  Acquiring: 'Acquiring',
  Extracting: 'Extracting',
  Comparing: 'Comparing',
  EvaluatingRules: 'Evaluating rules',
  Completed: 'Completed',
}

export type RunProgressStepperProps = {
  status: string
  startedUtc?: string
  createdUtc?: string
  completedUtc?: string
  targetStages?: string[]
  compact?: boolean
  className?: string
}

export default function RunProgressStepper({
  status,
  startedUtc,
  createdUtc,
  completedUtc,
  targetStages,
  compact = false,
  className = '',
}: RunProgressStepperProps) {
  const [nowMs, setNowMs] = useState(() => Date.now())
  const activeStep = resolveRunProgressStep(status, targetStages)
  const activeIndex = RUN_PROGRESS_STEPS.indexOf(activeStep)
  const clockStart = startedUtc || createdUtc
  const isActive = isActiveRunStatus(status) && !completedUtc

  useEffect(() => {
    if (!isActive) return undefined
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [isActive])

  const elapsed = useMemo(
    () => formatRunElapsed(clockStart, completedUtc, nowMs),
    [clockStart, completedUtc, nowMs],
  )

  return (
    <div className={`run-progress-stepper${compact ? ' run-progress-stepper--compact' : ''}${className ? ` ${className}` : ''}`}>
      <ol className="run-progress-stepper-track" aria-label="Compliance run progress">
        {RUN_PROGRESS_STEPS.map((step, index) => {
          const state = index < activeIndex ? 'done' : index === activeIndex ? 'active' : 'pending'
          return (
            <li key={step} className={`run-progress-step run-progress-step--${state}`}>
              <span className="run-progress-step-marker" aria-hidden />
              <span className="run-progress-step-label">{STEP_LABELS[step]}</span>
            </li>
          )
        })}
      </ol>
      {elapsed && (
        <div className="run-progress-stepper-elapsed">
          {completedUtc ? 'Elapsed' : 'Running'}: {elapsed}
        </div>
      )}
    </div>
  )
}
